import React, { MouseEvent } from 'react';
import classNames from 'classnames';
import Element from '@/components/common/element/Element';

import styles from './button-vintage.module.scss';

const BUTTON_CSS_COLORS = ['violet', 'orange', 'white'];

export interface IButtonVintageProps {
  /**
   * JSX | React Components inside Button
   */
  children?: any;
  /**
   * Button variant
   */
  variant?: 'fill' | 'border';
  /**
   * Button color
   */
  color?: 'violet' | 'orange' | 'white' | string;
  /**
   * Button text color
   */
  textColor?: 'violet' | 'orange' | 'white' | string;
  /**
   * Button type. Defines if button is used for landings
   */
  landing?: boolean;
  /**
   * Button shadow
   */
  noShadow?: boolean;

  /**
   * Button size
   */
  size?: 'l' | 'm' | 's' | 'xs';
  /**
   * Button text
   */
  text?: string;

  /**
   * Defines if Button is disabled
   */
  disabled?: boolean;
  /**
   * Defines an object with custom html props, not handled by React
   */
  html?: any;
  /**
   * Defines html tag for button component
   */
  htmlTag?: 'button' | 'a';
  /**
   * Defines href attribute for an anchor html tag
   */
  href?: string;
  /**
   * Button onClick handler function
   */
  onClick?: Function;
  /**
   * Button className attribute
   */
  className?: string;
  /**
   * Button style attribute
   */
  style?: {};
  /**
   * Ref to a node in DOM
   */
  ref?: React.Ref<SVGSVGElement>;
  /**
   * Is button has width: 100%
   */
  fullWidth?: boolean;
}

export const ButtonVintage: React.FC<IButtonVintageProps> = React.forwardRef(
  (props: IButtonVintageProps) => {
    const buttonRef = (props.ref) || React.createRef<HTMLElement>();

    const onClick = (event?: MouseEvent) => {
      if (!props.disabled && props.onClick) {
        props.onClick(event);
      }
    };

    const customStyles = {} as {
    color: string | undefined;
    backgroundColor: string | undefined;
    borderColor: string | undefined;
  };

    if (props.color && BUTTON_CSS_COLORS.indexOf(props.color) === -1) {
      switch (props.variant) {
        case 'fill':
          customStyles.backgroundColor = props.color;
          break;
        case 'border':
          customStyles.borderColor = props.color;
          if (!props.textColor) customStyles.color = props.color;
          break;
        default:
          break;
      }
    }

    if (props.textColor && BUTTON_CSS_COLORS.indexOf(props.textColor) === -1) {
      customStyles.color = props.textColor;
    }

    return (
      <Element
        ref={props.ref || buttonRef}
        disabled={props.disabled || false}
        htmlTag={props.htmlTag || 'button'}
        className={classNames(props.className, {
          [styles['button-vintage']]: true,
          [styles[`color-${props.color || 'violet'}`]]: props.color && BUTTON_CSS_COLORS.indexOf(props.color) !== -1,
          [styles[`variant-${props.variant || 'fill'}`]]: true,
          [styles[`size-${props.size || 'l'}`]]: true,
          [styles[`text-${props.textColor}`]]: props.textColor && BUTTON_CSS_COLORS.indexOf(props.textColor) !== -1,
          [styles['type-landing']]: !!props.landing,
          [styles['no-shadow']]: !props.noShadow,
          [styles['full-width']]: !!props.fullWidth,
        })}
        href={props.href}
        style={{
          ...props.style,
          ...customStyles,
        }}
        onClick={onClick}
        {...props.html}
      >
        {props.children}
        {props.text}
      </Element>
    );
  },
);

ButtonVintage.defaultProps = {
  color: 'violet',
};

export default ButtonVintage;
